import React, { useEffect, useMemo, useState } from 'react'
import { useDependencyDragContextOptional } from './DependencyContext'
import { useDragAutoScroll } from '../hooks/useDragAutoScroll'
import { useDragScrollOffset } from '../hooks/useDragScrollOffset'

// Line color during drag
const DRAG_LINE_COLOR = '#666666'
const CIRCLE_RADIUS = 5
const LINE_WIDTH = 2

interface DependencyDragOverlayProps {
  // Container element to attach the overlay to
  containerRef: React.RefObject<HTMLElement>
}

interface ContainerBounds {
  left: number
  top: number
  width: number
  height: number
}

/**
 * Overlay SVG for drawing dependency line preview during D&D
 * This is rendered as a fixed-position element that covers the gantt chart area
 */
export const DependencyDragOverlay: React.FC<DependencyDragOverlayProps> = ({
  containerRef,
}) => {
  const dragContext = useDependencyDragContextOptional()
  const [bounds, setBounds] = useState<ContainerBounds | null>(null)
  const isDragging = !!dragContext?.dragState.isDragging
  useDragAutoScroll(containerRef, isDragging)
  const scrollOffset = useDragScrollOffset(containerRef, isDragging)

  // Update container bounds when dragging starts
  useEffect(() => {
    const container = containerRef.current
    if (!container || !dragContext?.dragState.isDragging) {
      setBounds(null)
      return
    }

    // ヘッダーを除いた行エリアのみをカバーするため、ag-body-viewport の矩形を使用
    const bodyViewport = container.querySelector('.ag-body-viewport')
    const rect = bodyViewport
      ? bodyViewport.getBoundingClientRect()
      : container.getBoundingClientRect()
    setBounds({
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    })
  }, [containerRef, dragContext?.dragState.isDragging])

  // Handle global mouse move during drag
  useEffect(() => {
    if (!dragContext) return

    const handleMouseMove = (e: MouseEvent) => {
      if (dragContext.dragState.isDragging) {
        dragContext.updateDragPosition(e.clientX, e.clientY)
      }
    }

    const handleMouseUp = () => {
      // Use setTimeout to allow DropTargetCircle's onMouseUp to process first
      // If a valid drop occurred, endDrag() will already be called and isDragging will be false
      setTimeout(() => {
        if (dragContext.dragState.isDragging) {
          // No valid drop target - cancel the drag
          dragContext.endDrag()
        }
      }, 0)
    }

    if (dragContext.dragState.isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [dragContext?.dragState.isDragging, dragContext])

  // Calculate positions relative to container.
  // startX/startY はドラッグ開始時の clientX/clientY なので、
  // スクロール差分を減算して内容に追従させる。
  const linePositions = useMemo(() => {
    if (!dragContext?.dragState.isDragging || !bounds) return null

    const { startX, startY, currentX, currentY } = dragContext.dragState
    return {
      startX: startX - bounds.left - scrollOffset.x,
      startY: startY - bounds.top - scrollOffset.y,
      currentX: currentX - bounds.left,
      currentY: currentY - bounds.top,
    }
  }, [dragContext?.dragState, bounds, scrollOffset])

  if (!dragContext || !bounds || !linePositions) return null

  const { startX, startY, currentX, currentY } = linePositions
  const isVisible = dragContext.dragState.isDragging

  return (
    <svg
      style={{
        position: 'fixed',
        left: bounds.left,
        top: bounds.top,
        width: bounds.width,
        height: bounds.height,
        pointerEvents: 'none',
        zIndex: 1000,
        visibility: isVisible ? 'visible' : 'hidden',
      }}
    >
      {/* Dashed line from start to current position */}
      <line
        x1={startX}
        y1={startY}
        x2={currentX}
        y2={currentY}
        stroke={DRAG_LINE_COLOR}
        strokeWidth={LINE_WIDTH}
        strokeDasharray="5,5"
      />

      {/* Current position circle (stroked) */}
      <circle
        cx={currentX}
        cy={currentY}
        r={CIRCLE_RADIUS}
        fill="none"
        stroke={DRAG_LINE_COLOR}
        strokeWidth={LINE_WIDTH}
      />
    </svg>
  )
}
